/**
 * Internal type definitions for Userpilot event tracking
 * These types are used internally and should not be imported directly by consumers.
 */

import type {
  FeedbackRating,
  FeedbackReasonCode,
} from '../../api-clients/sessions';

/**
 * Generic tracked event type with built-in parameter type information
 * The _params field is a phantom type used only for type inference
 */
export type TrackedEvent<TParams = void> = {
  readonly name: string;
  readonly _params?: TParams;
};

/**
 * Base event parameters shared across all events
 *
 * These fields are automatically populated by useUserpilotTracking hook.
 */
export interface BaseEventParams {
  userId?: string;

  customerId?: string;
  brand?: string;
  market?: string;

  // Event metadata (auto-populated by core.ts)
  timestamp?: string;
}

/**
 * Recording event parameters
 */
export interface RecordingEventParams extends BaseEventParams {
  sessionId?: string | null;
  encounterId?: string;
  deviceId?: string;
  deviceLabel?: string;
  templateId?: string;
  templateName?: string;
  chunkNumber?: number;
  bytes?: number;
  boundBytes?: number;
  failureReason?: string;
  resumeConfirmationReason?: 'provider' | 'device' | 'both' | 'unknown';
}

/**
 * Document action parameters
 */
export interface DocumentActionParams extends BaseEventParams {
  documentId?: string;
  templateId?: string;
  templateName?: string;
  sessionId?: string | null;
  documentLength?: number;
}

/**
 * Document generation parameters
 * Tracks successful document generation from recording workflow
 */
export interface DocumentGenerationParams extends BaseEventParams {
  sessionId?: string | null;
  transcriptLength?: number; // Character count of transcript
  wordCount?: number;
}

/**
 * Recording restarted parameters
 * Tracks when a user restarts a recording session
 */
export interface RecordingRestartedParams extends BaseEventParams {
  sessionId?: string | null;
  encounterId?: string;
  previousSessionId?: string;
}

/**
 * Template selection parameters
 */
export interface TemplateSelectionParams extends BaseEventParams {
  templateId?: string;
  templateName?: string;
  previousTemplateId?: string;
  previousTemplateName?: string;
  changed?: boolean;
}

/**
 * Existing session viewed parameters
 * Tracks when a user opens the widget with existing session data
 */
export interface ExistingSessionViewedParams extends BaseEventParams {
  sessionId?: string;
  encounterId?: string;
  hasTranscript: boolean;
  hasDocument: boolean;
  transcriptSegmentCount?: number;
  documentCount?: number;
}

/**
 * Note feedback parameters: IDs and enums only. The comment text is never
 * tracked; `hasComment` says only whether one was sent.
 */
export interface NoteFeedbackParams extends BaseEventParams {
  sessionId?: string | null;
  documentId?: string;
  feedbackId?: string;
  rating?: FeedbackRating;
  previousRating?: FeedbackRating;
  reasonCodes?: FeedbackReasonCode[];
  hasComment?: boolean;
  dismissMethod?: 'skip' | 'close';
}

/**
 * Helper function to create a tracked event with type information
 * This ensures type safety and provides a clean API for defining events
 */
export function createEvent<TParams>(name: string): TrackedEvent<TParams> {
  return { name } as TrackedEvent<TParams>;
}
